import { Injectable } from '@angular/core';
import { AlertService } from '../alert/alert.service';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { environment } from '../../../environments/environment';

const errorsCode = {
    'auth/argument-error': '',
    'auth/app-not-authorized':
        "Cette application, identifiée par le domaine où elle est hébergée, n'est pas autorisée à utiliser Firebase Authentication avec la clé API fournie. Passez en revue la configuration de votre clé dans la console de l'API Google.",
    'auth/app-not-installed':
        "L'application mobile demandée correspondant à l'identifiant (nom de paquet Android ou ID de paquet iOS) fourni n'est pas installée sur cet appareil.",
    'auth/captcha-check-failed': '',
    'auth/code-expired': '',
    'auth/cordova-not-ready': "Cordova n'est pas prêt.",
    'auth/cors-unsupported': "Ce navigateur n'est pas supporté.",
    'auth/credential-already-in-use':
        "Ce justificatif d'identité est déjà associé à un autre compte utilisateur.",
    'auth/custom-token-mismatch':
        'Le jeton personnalisé correspond à un public différent.',
    'auth/requires-recent-login':
        'Cette opération est sensible et nécessite une authentification récente. Connectez-vous à nouveau avant de réessayer cette demande.',
    'auth/dynamic-link-not-activated':
        'Veuillez activer les liens dynamiques dans la console Firebase et accepter les termes et conditions.',
    'auth/email-already-in-use':
        "L'adresse e-mail est déjà utilisée par un autre compte.",
    'auth/expired-action-code': "Le code d'action a expiré.",
    'auth/cancelled-popup-request':
        "Cette opération a été annulée en raison de l'ouverture d'une autre fenêtre contextuelle conflictuelle.",
    'auth/internal-error': "Une erreur interne s'est produite.",
    'auth/invalid-app-credential': '',
    'auth/invalid-app-id':
        "L'identifiant de l'application mobile n'est pas enregistré pour le projet en cours.",
    'auth/invalid-user-token':
        "L'identifiant de l'utilisateur n'est plus valide. L'utilisateur doit se reconnecter.",
    'auth/invalid-auth-event': "Une erreur interne s'est produite.",
    'auth/invalid-verification-code': '',
    'auth/invalid-cordova-configuration':
        "Les plugins Cordova suivants doivent être installés pour permettre à OAuth d'ouvrir une session : cordova-plugin-buildinfo, cordova-universal-links-plugin, cordova-plugin-browsertab, cordova-plugin-inappbrowser et cordova-plugin-customurlscheme.",
    'auth/invalid-custom-token':
        'Le format du jeton personnalisé est incorrect. Veuillez consulter la documentation.',
    'auth/invalid-email': "L'adresse e-mail est mal formatée.",
    'auth/invalid-api-key':
        "Votre clé API n'est pas valide, veuillez vérifier que vous l'avez copiée correctement.",
    'auth/invalid-credential':
        "Le certificat d'authenticité fourni est malformé ou a expiré.",
    'auth/invalid-message-payload':
        "Le modèle d'e-mail correspondant à cette action contient des caractères non valides dans son message. Veuillez réparer en allant dans la section Auth email templates dans la Firebase Console.",
    'auth/invalid-oauth-provider':
        "EmailAuthProvider n'est pas supporté pour cette opération. Cette opération ne prend en charge que les fournisseurs OAuth.",
    'auth/unauthorized-domain':
        "Ce domaine n'est pas autorisé pour les opérations OAuth pour votre projet Firebase. Modifiez la liste des domaines autorisés à partir de la console Firebase.",
    'auth/invalid-action-code':
        "Le code d'action n'est pas valide. Cela peut se produire si le code est malformé, expiré ou a déjà été utilisé.",
    'auth/wrong-password':
        "Le mot de passe n'est pas valide ou l'utilisateur n'a pas de mot de passe.",
    'auth/invalid-identifier-number': '',
    'auth/invalid-recipient-email':
        "L'email correspondant à cette action n'a pas été envoyé car l'adresse email du destinataire fournie n'est pas valide.",
    'auth/invalid-sender':
        "Le modèle d'e-mail correspondant à cette action contient un e-mail ou un nom d'expéditeur invalide. Veuillez réparer en allant dans la section Auth email templates dans la Firebase Console.",
    'auth/invalid-verification-id': '',
    'auth/missing-iframe-start': "Une erreur interne s'est produite.",
    'auth/auth-domain-config-required':
        "Assurez-vous d'inclure authDomain lorsque vous appelez firebase.initializeApp(), en suivant les instructions de la console Firebase.",
    'auth/missing-app-credential': '',
    'auth/missing-verification-code': '',
    'auth/missing-identifier-number': '',
    'auth/missing-verification-id': '',
    'auth/app-deleted': 'Cette instance de FirebaseApp a été supprimée.',
    'auth/account-exists-with-different-credential':
        "Un compte existe déjà avec la même adresse email mais avec des identifiants de connexion différents. Connectez-vous à l'aide d'un fournisseur associé à cette adresse e-mail.",
    'auth/network-request-failed':
        "Une erreur réseau (telle qu'un délai d'attente, une connexion interrompue ou un hôte inaccessible) s'est produite.",
    'auth/no-auth-event': "Une erreur interne s'est produite.",
    'auth/no-such-provider':
        "L'utilisateur n'était pas lié à un compte chez le fournisseur donné.",
    'auth/operation-not-allowed':
        "Le fournisseur d'accès donné est désactivé pour ce projet Firebase. Activez-le dans la console Firebase, sous l'onglet Méthode de connexion de la section Auth.",
    'auth/operation-not-supported-in-this-environment':
        "Cette opération n'est pas prise en charge dans l'environnement sur lequel l'application s'exécute. 'location.protocol' doit être http, https ou chrome-extension et le stockage web doit être activé.",
    'auth/popup-blocked':
        "Impossible d'établir une connexion avec le popup. Il se peut qu'il ait été bloqué par le navigateur.",
    'auth/popup-closed-by-user':
        "Le popup a été fermé par l'utilisateur avant de terminer l'opération.",
    'auth/provider-already-linked':
        "L'utilisateur ne peut être lié qu'à une seule identité pour le fournisseur donné.",
    'auth/quota-exceeded': '',
    'auth/redirect-cancelled-by-user':
        "L'opération de redirection a été annulée par l'utilisateur avant d'être finalisée.",
    'auth/redirect-operation-pending':
        'Une opération de redirection est déjà en cours.',
    'auth/timeout': "L'opération a dépassé le délai.",
    'auth/user-token-expired':
        "L'identifiant de l'utilisateur n'est plus valide. L'utilisateur doit se reconnecter.",
    'auth/too-many-requests':
        "Nous avons bloqué toutes les demandes provenant de cet appareil en raison d'une activité inhabituelle. Réessayez plus tard.",
    'auth/user-cancelled':
        "L'utilisateur n'a pas accordé à votre application les permissions demandées.",
    'auth/user-not-found':
        "Il n'y a pas d'enregistrement utilisateur correspondant à cet identifiant. L'utilisateur peut avoir été supprimé.",
    'auth/user-disabled':
        'Le compte utilisateur a été désactivé par un administrateur.',
    'auth/user-mismatch':
        "Les informations d'identification fournies ne correspondent pas à l'utilisateur précédemment connecté.",
    'auth/user-signed-out': '',
    'auth/weak-password':
        'Le mot de passe doit comporter au moins 6 caractères.',
    'auth/web-storage-unsupported':
        "Ce navigateur n'est pas pris en charge ou les cookies et données de tiers peuvent être désactivés.",
};

/**
 * Instance de firebase, nécessaire pour la connexion
 */
const firebaseApp = firebase.initializeApp(environment.firebase);

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    constructor(private alert$: AlertService) {}

    onGoogleAuth(callback: Function) {
        return new Promise<any>(() => {
            const googleProvider = new firebase.auth.GoogleAuthProvider();
            googleProvider.addScope('profile');
            googleProvider.addScope('email');
            googleProvider.addScope('phone');
            return firebase
                .auth()
                .signInWithPopup(googleProvider)
                .then((userCredentials) => {
                    callback(userCredentials.user);
                })
                .catch((e) => {
                    this.firebaseErrorAuth(e);
                    callback(null);
                });
        });
    }

    onFacebookAuth(callback: Function) {
        return new Promise<any>(() => {
            const facebookProvider = new firebase.auth.FacebookAuthProvider();
            return firebase
                .auth()
                .signInWithPopup(facebookProvider)
                .then((userCredentials) => {
                    callback(userCredentials.user);
                })
                .catch((e) => {
                    this.firebaseErrorAuth(e);
                    callback(null);
                });
        });
    }

    /**
     * @param callback
     * @returns
     */
    onTwitterAuth(callback: Function) {
        return new Promise<any>(() => {
            const twitterProvider = new firebase.auth.TwitterAuthProvider();
            // firebase.
            return firebase
                .auth()
                .signInWithPopup(twitterProvider)
                .then((userCredentials) => {
                    callback(userCredentials.user);
                })
                .catch((e) => {
                    this.firebaseErrorAuth(e);
                    callback(null);
                });
        });
    }

    onMicrosoftAuth(callback: Function) {
        return new Promise<any>(() => {
            const microsoftProvider = new firebase.auth.OAuthProvider(
                'microsoft.com'
            );
            microsoftProvider.addScope('email');
            microsoftProvider.addScope('openid');
            return firebase
                .auth()
                .signInWithPopup(microsoftProvider)
                .then((userCredentials) => {
                    callback(userCredentials.user);
                })
                .catch((e) => {
                    this.firebaseErrorAuth(e);
                    callback(null);
                });
        });
    }

    private firebaseErrorAuth(e: any) {
        if (e.code === 'auth/account-exists-with-different-credential') {
            this.alert$.show({
                message:
                    'Erreur - Vos identifiants existent déjà sur une autre plateforme, veuillez sélectionner une autre méthode ou changer de compte',
                duration: 5000,
            });
            return;
        }

        if (errorsCode.hasOwnProperty(e.code)) {
            console.error('AuthService - ', errorsCode[e.code]);
        } else {
            console.error('AuthService - Unknown Error');
        }
    }
}
